import * as React from 'react';
import { type EveryComponentSize, type ExtraButton, ModalComponent, ModalVariant, Text } from 'ui';
import styles from './confirm-modal.module.css';

interface ConfirmModalProps {
    openModal: boolean;
    shouldBlockBeforeUnload: boolean;
    /** Visible heading and, unless `ariaLabel` is set, the dialog's accessible name. Empty renders no heading. */
    title: string;
    /** Accessible name for a modal without a visible title. */
    ariaLabel?: string;
    subTitle?: string;
    variant?: ModalVariant;
    message?: React.ReactNode;
    size?: EveryComponentSize;
    showCancelButton?: boolean;
    customConfirmLabel?: string;
    customCancelLabel?: string;
    handleClose?: () => void;
    handleCancel: () => void;
    handleConfirm: () => void;
    extraButton?: ExtraButton;
    testId?: string;
    /** Where focus goes on close when the control that opened the modal is gone. */
    onOpenerRemoved?: () => void;
    /** Stacks the modal above other open overlays, e.g. a legacy MUI dialog. */
    aboveOverlays?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = (props) => {
    const {
        openModal,
        shouldBlockBeforeUnload,
        title,
        ariaLabel,
        subTitle,
        variant = 'destruct',
        message,
        size = '3xl',
        showCancelButton = false,
        customConfirmLabel,
        customCancelLabel,
        handleClose = () => null,
        handleCancel = () => null,
        handleConfirm,
        extraButton,
        testId,
        onOpenerRemoved,
        aboveOverlays,
    } = props;
    const descriptionId = React.useId();

    React.useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (shouldBlockBeforeUnload) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [shouldBlockBeforeUnload]);

    return (
        <ModalComponent
            className={styles['confirm-modal']}
            openModal={openModal}
            variant={variant}
            size={size}
            ariaLabel={ariaLabel ?? title}
            ariaDescribedBy={descriptionId}
            testId={testId}
            dismissible
            showHeader={false}
            handleClose={handleClose}
            handleCancel={handleCancel}
            handleConfirm={handleConfirm}
            showCloseButton={false}
            showCancelButton={showCancelButton}
            customConfirmLabel={customConfirmLabel}
            customCancelLabel={customCancelLabel}
            extraButton={extraButton}
            onOpenerRemoved={onOpenerRemoved}
            aboveOverlays={aboveOverlays}
        >
            <div className={styles['confirm-modal-body']}>
                {title && <Text variant="title-large">{title}</Text>}
                <div id={descriptionId} className={styles['confirm-modal-description']}>
                    {subTitle && <Text variant="body-large">{subTitle}</Text>}
                    {message && <Text variant="body-large">{message}</Text>}
                </div>
            </div>
        </ModalComponent>
    );
};
